import { ButtonBase, Collapse } from '@mui/material';
import QuizQuestionCard from 'components/ViewQuiz/QuizQuestionCard';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Icon, RubricTable, Stack, styled, Text } from 'shell/components';
import { useViewAccordionRubricTableButton } from 'shell/components/ViewRubricTable';
import { SYSTEM_ASSIGNMENT_DEFAULTS } from 'utils/assignmentDefaults';
import { getLanguageLevelText, getSuggestionLengthText, getToneText, stripHTML } from 'utils/helper';
import { isQuizFeedbackSettingsDefault } from 'utils/quizFeedback';
import { hasQuizRubric, QuizQuestionMode, QuizRubricType } from 'utils/quizRubric';

const QuizDetails = ({ quiz }) => {
    const { t } = useTranslation('quiz');
    const { t: assignmentT } = useTranslation('assignment');
    const { AccordionRubricTableButton, isExpanded } = useViewAccordionRubricTableButton();
    const [feedbackExpanded, setFeedbackExpanded] = useState(false);
    const rubric = quiz.rubric;
    const criteria = rubric?.criteria || [];
    const questions = Array.isArray(quiz.questions)
        ? [...quiz.questions].sort((first, second) => first.position - second.position)
        : [];
    const timelyQuestions = questions.filter(({ ai_gradable }) => ai_gradable);
    const autoGradedQuestions = questions.filter(({ ai_gradable }) => !ai_gradable);
    const feedbackSettings = {
        ...SYSTEM_ASSIGNMENT_DEFAULTS.feedbackSettings,
        structure: quiz.ai_feedback_settings?.structure ?? SYSTEM_ASSIGNMENT_DEFAULTS.feedbackSettings.structure,
        numSuggestions:
            quiz.ai_feedback_settings?.num_suggestions ?? SYSTEM_ASSIGNMENT_DEFAULTS.feedbackSettings.numSuggestions,
        suggestionLength:
            quiz.ai_feedback_settings?.suggestion_length ??
            SYSTEM_ASSIGNMENT_DEFAULTS.feedbackSettings.suggestionLength,
        languageLevel:
            quiz.ai_feedback_settings?.language_level ?? SYSTEM_ASSIGNMENT_DEFAULTS.feedbackSettings.languageLevel,
        tone: quiz.ai_feedback_settings?.tone ?? SYSTEM_ASSIGNMENT_DEFAULTS.feedbackSettings.tone,
        templated: quiz.ai_feedback_settings?.templated ?? null,
        customInstructions: quiz.ai_feedback_settings?.custom_instructions || '',
        feedbackExamples: quiz.ai_feedback_settings?.feedback_examples || [''],
    };
    const feedbackIsDefault = isQuizFeedbackSettingsDefault(feedbackSettings, quiz.ai_feedback_context);
    const feedbackDetailsId = 'quiz-advanced-feedback-details';
    const structureLabel =
        {
            rubric: t('assignment:feedback-section.rubric-based-feedback', 'Rubric-Based Feedback'),
            system: t('assignment:feedback-section.system-based-feedback', 'General & Detailed Feedback'),
            templated: t('assignment:feedback-section.templated-feedback', 'Smart Feedback Templates'),
            custom: t('assignment:feedback-section.custom-feedback', 'Custom Feedback'),
        }[feedbackSettings.structure] || feedbackSettings.structure;
    const rubricTypeLabel =
        quiz.rubric_type === QuizRubricType.QUESTION
            ? t('rubric.question', 'Question By Question Rubric')
            : t('rubric.holistic', 'Holistic Rubric');
    const questionModeLabel =
        quiz.question_mode === QuizQuestionMode.STANDALONE
            ? t('rubric.standalone', 'Standalone Questions')
            : t('rubric.interdependent', 'Interdependent Questions');

    const renderQuestionGroup = (id, label, questions, timelyGraded) => {
        if (!questions.length) return null;
        return (
            <Stack component={'section'} aria-labelledby={id} spacing={2}>
                <Text id={id} color={'light'} weight={'medium'}>
                    {label}
                </Text>
                <Stack spacing={5}>
                    {questions.map((question) => (
                        <QuizQuestionCard
                            key={question.id}
                            question={question}
                            criteria={criteria}
                            rubricType={quiz.rubric_type}
                            timelyGraded={timelyGraded}
                        />
                    ))}
                </Stack>
            </Stack>
        );
    };

    return (
        <Stack spacing={5}>
            <Stack component={'section'} aria-labelledby={'quiz-rubric-heading'} spacing={2}>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} spacing={4}>
                    <Text id={'quiz-rubric-heading'} component={'h2'} weight={'medium'}>
                        {t('rubric.title', 'Quiz Rubric')}
                    </Text>
                    {hasQuizRubric(rubric) && <AccordionRubricTableButton />}
                </Stack>
                {hasQuizRubric(rubric) ? (
                    <RubricTable type={'view'} rubric={rubric} isExpanded={isExpanded} data={criteria} />
                ) : (
                    <Text color={'light'}>{t('common:ui.none', 'None')}</Text>
                )}
            </Stack>

            <SettingsCard component={'section'} aria-label={t('view.settings.title', 'Quiz Settings')} spacing={4}>
                <Stack spacing={2}>
                    <Text weight={'medium'}>{t('rubric.type', 'Rubric Type')}</Text>
                    <IndentedText>{rubricTypeLabel}</IndentedText>
                </Stack>
                <Stack spacing={2}>
                    <Text weight={'medium'}>{t('view.settings.quiz-type', 'Quiz Type')}</Text>
                    <IndentedText>{questionModeLabel}</IndentedText>
                </Stack>
            </SettingsCard>

            <Stack component={'section'} aria-labelledby={'quiz-feedback-heading'} spacing={2}>
                <Text id={'quiz-feedback-heading'} component={'h2'} weight={'medium'}>
                    {t('view.feedback.title', 'Feedback')}
                </Text>
                <Divider />
                <Stack direction={'row'} spacing={2}>
                    <Text>{t('view.feedback.quiz-feedback', 'Quiz Feedback:')}</Text>
                    <Text color={'light'}>
                        {quiz.ai_feedback_enabled
                            ? t('view.feedback.enabled', 'Enabled')
                            : t('view.feedback.disabled', 'Disabled')}
                    </Text>
                </Stack>
                {quiz.ai_feedback_enabled && (
                    <>
                        <FeedbackButton
                            aria-controls={feedbackDetailsId}
                            aria-expanded={feedbackExpanded}
                            onClick={() => setFeedbackExpanded((current) => !current)}
                        >
                            <Text weight={'medium'}>
                                {t('view.feedback.advanced-settings', 'Advanced Feedback Settings')}
                            </Text>
                            <Icon kind={feedbackExpanded ? 'chevronUp' : 'chevronDown'} sx={{ fontSize: 16 }} />
                        </FeedbackButton>
                        <Collapse in={feedbackExpanded} timeout={200} unmountOnExit>
                            <FeedbackDetails id={feedbackDetailsId} spacing={5}>
                                <Stack>
                                    <Text color={'light'}>
                                        {t(
                                            'view.feedback.advanced-summary',
                                            'Advanced settings: {{structure}} ({{status}})',
                                            {
                                                structure: structureLabel,
                                                status: feedbackIsDefault
                                                    ? t('assignment:system-default', 'System Default')
                                                    : t('assignment:edited', 'Edited'),
                                            }
                                        )}
                                    </Text>
                                    {feedbackSettings.structure === 'custom' ? (
                                        <Text color={'light'} rich>
                                            {feedbackSettings.customInstructions || t('common:ui.none', 'None')}
                                        </Text>
                                    ) : (
                                        <SummaryList>
                                            <li>
                                                <Text component={'span'} color={'light'}>
                                                    {t(
                                                        'view.feedback.suggestion-length',
                                                        'Length of each suggestion: {{value}}',
                                                        {
                                                            value: getSuggestionLengthText(
                                                                feedbackSettings.suggestionLength,
                                                                t
                                                            ),
                                                        }
                                                    )}
                                                </Text>
                                            </li>
                                            <li>
                                                <Text component={'span'} color={'light'}>
                                                    {t('view.feedback.language-level', 'Language level: {{value}}', {
                                                        value: getLanguageLevelText(
                                                            feedbackSettings.languageLevel,
                                                            assignmentT
                                                        ),
                                                    })}
                                                </Text>
                                            </li>
                                            <li>
                                                <Text component={'span'} color={'light'}>
                                                    {t('view.feedback.tone', 'Feedback tone: {{value}}', {
                                                        value: getToneText(feedbackSettings.tone, assignmentT),
                                                    })}
                                                </Text>
                                            </li>
                                        </SummaryList>
                                    )}
                                </Stack>
                                <Text color={'light'}>
                                    {t(
                                        'view.feedback.additional-instructions',
                                        'Additional instructions: “{{instructions}}”',
                                        {
                                            instructions:
                                                stripHTML(quiz.ai_feedback_context || '').trim() ||
                                                t('common:ui.none', 'None'),
                                        }
                                    )}
                                </Text>
                            </FeedbackDetails>
                        </Collapse>
                    </>
                )}
            </Stack>

            <Stack component={'section'} aria-labelledby={'quiz-questions-heading'} spacing={2}>
                <Text id={'quiz-questions-heading'} component={'h2'} weight={'medium'}>
                    {t('create.questions', 'Questions')}
                </Text>
                <Divider />
                <Stack spacing={5}>
                    {renderQuestionGroup(
                        'timely-graded-questions-heading',
                        quiz.rubric_type === QuizRubricType.QUESTION
                            ? t('view.questions.timely-graded', 'For TimelyGrader Grading')
                            : t('create.subjective', 'Subjective Questions'),
                        timelyQuestions,
                        true
                    )}
                    {renderQuestionGroup(
                        'auto-graded-questions-heading',
                        t('create.auto-graded', 'Auto-Graded Questions'),
                        autoGradedQuestions,
                        false
                    )}
                    {!questions.length && (
                        <Text color={'light'}>
                            {t('create.no-questions', 'This quiz has no questions to display.')}
                        </Text>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};

const SettingsCard = styled(Stack)(({ theme }) => ({
    padding: theme.spacing(4),
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    borderRadius: '16px',
}));

const IndentedText = styled(Text)(({ theme }) => ({
    paddingLeft: theme.spacing(4),
}));

const FeedbackButton = styled(ButtonBase)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    margin: theme.spacing(-1),
    borderRadius: '4px',
    '&:focus-visible': {
        outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
        outlineOffset: '2px',
    },
}));

const FeedbackDetails = styled(Stack)(({ theme }) => ({
    width: '100%',
    padding: theme.spacing(3),
    borderRadius: '8px',
    background: theme.palette.ensightful.neutral[50],
}));

const SummaryList = styled('ul')(({ theme }) => ({
    margin: 0,
    paddingLeft: theme.spacing(6),
}));

export default QuizDetails;
